import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { CircularProgress } from 'src/components/CircularProgress';
import { Dialog } from 'src/components/Dialog';
import { Stack } from 'src/components/Stack';
import { DataTable } from 'src/components/Table/DataTable';
import { Typography } from 'src/components/Typography';
import {
  fetchVersionHistory,
  versionQueryKeys,
} from 'src/models/version/version';
import { Identifier } from 'src/types/utilityTypes';
import { formatFullDateAndTime } from 'src/utils/dateFormatter';

interface VersionHistoryProps {
  open: boolean;
  onClose: () => void;
  itemType: string;
  itemId: Identifier;
}

export const VersionHistory: React.FC<VersionHistoryProps> = ({
  open,
  onClose,
  itemType,
  itemId,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'visits.versionHistory',
  });

  const {
    data: versions,
    isLoading,
    error,
  } = useQuery({
    queryKey: versionQueryKeys.versions(itemType, itemId),
    queryFn: () => fetchVersionHistory(itemType, itemId),
    enabled: open && !!itemType && !!itemId,
    select: (data) => [...data].reverse(),
  });

  if (isLoading) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        title={t('Version History')}
        maxWidth="md"
        fullWidth
      >
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        title={t('Version History')}
        maxWidth="md"
        fullWidth
      >
        <Typography color="error" textAlign="center" p={4}>
          {t('Failed to load version history')}
        </Typography>
      </Dialog>
    );
  }

  const columns = [
    {
      id: 'createdAt',
      label: t('Date'),
      format: (value: string) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">
            {formatFullDateAndTime(value)}
          </Typography>
        </Stack>
      ),
      minWidth: '180px',
    },
    {
      id: 'whodunnit',
      label: t('Account'),
      format: (user: any) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">{user?.name || t('Unknown')}</Typography>
        </Stack>
      ),
      minWidth: '140px',
    },
    {
      id: 'changes',
      label: t('Changes'),
      format: (changes: string[]) => (
        <Box>
          {changes && changes.length > 0 ? (
            <Stack spacing={0.5}>
              {changes.map((change, changeIndex) => (
                <Typography
                  key={changeIndex}
                  variant="body2"
                  color="text.secondary"
                  sx={{ position: 'relative' }}
                >
                  {change}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('Unknown changes')}
            </Typography>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('Version History')}
      maxWidth="lg"
      fullWidth
      actions={[
        {
          label: t('Close'),
          onClick: onClose,
          color: 'primary',
        },
      ]}
    >
      <DataTable
        columns={columns}
        rows={versions || []}
        loading={isLoading}
        noResultsText={t('No version history found')}
      />
    </Dialog>
  );
};
